import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { useState } from 'react';
import { Breadcrumb, BreadcrumbLink, BreadcrumbPage } from '@/ui/components/breadcrumb';
import { Header, HeaderBackButton } from '@/ui/components/header';
import { Icon } from '@/ui/components/icon';
import {
  FileBookmarkIcon,
  MoreHorizontalIcon,
  PencilEdit02Icon,
  PlusSignIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import { createStepper, useStepper } from '@/ui/components/stepper';
import { Field, FieldControl, FieldError, FieldItem, FieldLabel } from '@/ui/components/form/field';
import { RadioGroup, RadioGroupLabel } from '@/ui/components/radio-group';
import { RadioGroupItem } from '@/ui/components/radio-group';
import { FieldsetLegend, Fieldset } from '@/ui/components/form/fieldset';
import {
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectTriggerIcon,
  SelectValue,
} from '@/ui/components/select';
import { useCreateExtractionsListTemplate } from '@/features/extractions/mutations';
import {
  extractionListTemplatesQuery,
  extractionListTemplateQuery,
  extractionTemplatesQuery,
} from '@/features/extractions/queries';
import { useQuery } from '@tanstack/react-query';
import { useCreateFolder } from '@/features/folders/mutations';
import { Checkbox } from '@/ui/components/checkbox';
import { useAtom, useSetAtom } from 'jotai';
import { DroppedItem } from '@/ui/types';
import { DndContext, DragOverlay, UniqueIdentifier } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { createPortal } from 'react-dom';
import { ExtractionDropzone } from '@/features/extractions/create-extraction-template/extraction-dropzone';
import { SavedExtractionsList } from '@/features/extractions/create-extraction-template/saved-extractions-list';
import { SavedExtractionsListItem } from '@/features/extractions/create-extraction-template/saved-extractions-list-item';
import { usePosthogTracking } from '@/features/posthog/helpers';
import { MainScrollArea } from '@/ui/components/main';
import { CreateExtractionTemplate } from '@/features/extractions/create-extraction-template/create-extraction-template';
import { useNavigate } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { H1 } from '@/ui/components/heading';
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@/ui/components/menu';
import { Button } from '@/ui/components/button';
import { addOrganizationExtractionOpenAtom } from '@/features/extractions/store';
import {
  extractionsAtom,
  folderNameAtom,
  modeAtom,
  nameAtom,
  saveInFolderAtom,
  selectedTemplateIdAtom,
} from '@/features/extractions/create-extraction-template/store';

const {
  Stepper,
  StepperStep,
  StepperStepTitle,
  StepperStepDescription,
  StepperNavigation,
  StepperBack,
  StepperNext,
  StepperCancel,
  StepperStepScrollArea,
} = createStepper(['template_setup', 'add_extractions']);

export const Route = createFileRoute('/_authed/settings/templates/new')({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <>
      <Header>
        <HeaderBackButton />
        <Breadcrumb>
          <BreadcrumbLink to={'/settings/templates'}>
            <Icon icon={FileBookmarkIcon} />
            {t({ en: 'Templates', nb: 'Maler' })}
          </BreadcrumbLink>
          <BreadcrumbPage>
            {t({ en: 'New extraction template', nb: 'Ny uttrekksmal' })}
          </BreadcrumbPage>
        </Breadcrumb>
      </Header>
      <MainScrollArea viewportClassName="flex grow md:bg-surface-2 md:p-3 min-h-0">
        <Stepper storageKey="create_template">
          <TemplateSetupStep />
          <AddExtractionsStep />
          <CreateExtractionTemplate />
        </Stepper>
      </MainScrollArea>
    </>
  );
}

function TemplateSetupStep() {
  const stepper = useStepper();
  const auth = useAuth();
  const organizationId = auth.user.organizationId;
  const [name, setName] = useAtom(nameAtom);
  const [mode, setMode] = useAtom(modeAtom);
  const [selectedTemplateId, setSelectedTemplateId] = useAtom(selectedTemplateIdAtom);
  const setExtractions = useSetAtom(extractionsAtom);
  const { trackPosthogEvent } = usePosthogTracking();

  const { data: existingTemplates } = useQuery(extractionListTemplatesQuery(organizationId));

  const { data: selectedTemplateData } = useQuery({
    ...extractionListTemplateQuery(organizationId, selectedTemplateId!),
    enabled: !!selectedTemplateId && mode === 'duplicate',
  });

  return (
    <StepperStep
      id="template_setup"
      onFormSubmit={() => {
        if (mode === 'duplicate' && selectedTemplateData?.templates) {
          trackPosthogEvent('EXTRACTIONS_TEMPLATE_COPIED');
          setExtractions(
            selectedTemplateData.templates.map((template, index) => ({
              userQuery: template.userQuery,
              customTopicTemplateId: template.customTopicTemplateId,
              customTopicTemplateOrder: index,
              id: `dropped-${template.customTopicTemplateId}`,
            })),
          );
        }
        stepper.goNext();
      }}
      className={'max-md:rounded-none max-md:border-transparent'}
    >
      <StepperStepScrollArea>
        <StepperStepTitle>{t({ en: 'Template setup', nb: 'Oppsett av mal' })}</StepperStepTitle>
        <StepperStepDescription>
          {t({
            en: 'Enter template details and choose creation mode',
            nb: 'Oppgi maldetaljer og velg opprettingsmodus',
          })}
        </StepperStepDescription>
        <Field className={'max-w-sm'}>
          <FieldLabel>{t({ en: 'Name', nb: 'Navn på uttrekksmal' })}</FieldLabel>
          <FieldControl
            autoFocus
            value={name}
            onValueChange={setName}
            required
            placeholder={t({ en: 'Enter name', nb: 'Navn på uttrekksmal' })}
          />
          <FieldError />
        </Field>
        <Field>
          <Fieldset
            render={<RadioGroup value={mode} onValueChange={(v) => setMode(v as typeof mode)} />}
          >
            <FieldsetLegend className={'md:mb-2 text-sm inline-block font-medium'}>
              {t({ en: 'Creation mode', nb: 'Opprettingsmodus' })}
            </FieldsetLegend>
            <FieldItem>
              <RadioGroupLabel>
                <RadioGroupItem value="createNew"></RadioGroupItem>
                {t({ en: 'Create new', nb: 'Opprett ny' })}
              </RadioGroupLabel>
            </FieldItem>
            <FieldItem>
              <RadioGroupLabel>
                <RadioGroupItem value="duplicate"></RadioGroupItem>
                {t({ en: 'Duplicate and edit template', nb: 'Dupliser og rediger mal' })}
              </RadioGroupLabel>
            </FieldItem>
          </Fieldset>
        </Field>
        {mode === 'duplicate' && (
          <Field className={'max-w-sm'}>
            <FieldLabel>
              {t({ en: 'Select template to duplicate', nb: 'Velg mal som skal dupliseres' })}
            </FieldLabel>
            <Select
              required
              value={selectedTemplateId}
              onValueChange={(v) => setSelectedTemplateId(v)}
            >
              <SelectTrigger size="lg" className="w-full">
                <SelectValue>
                  {(v) =>
                    existingTemplates?.find((item) => item.id === v)?.name ??
                    t({ en: 'Select a template...', nb: 'Velg en mal...' })
                  }
                </SelectValue>
                <SelectTriggerIcon />
              </SelectTrigger>
              <SelectPopup>
                {existingTemplates?.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
            <FieldError />
          </Field>
        )}
      </StepperStepScrollArea>
      <StepperNavigation>
        <StepperCancel
          nativeButton={false}
          render={<Link to={'/settings/templates'}>{t(common.cancel)}</Link>}
        />
        <StepperNext />
      </StepperNavigation>
    </StepperStep>
  );
}

function AddExtractionsStep() {
  const navigate = useNavigate();
  const stepper = useStepper();
  const auth = useAuth();
  const organizationId = auth.user.organizationId;
  const { trackPosthogEvent } = usePosthogTracking();

  const [name] = useAtom(nameAtom);
  const setName = useSetAtom(nameAtom);
  const setMode = useSetAtom(modeAtom);
  const setSelectedTemplateId = useSetAtom(selectedTemplateIdAtom);
  const [extractions, setExtractions] = useAtom(extractionsAtom);
  const setIsCreateDialogOpen = useSetAtom(addOrganizationExtractionOpenAtom);
  const [saveInFolder, setSaveInFolder] = useAtom(saveInFolderAtom);
  const [folderName, setFolderName] = useAtom(folderNameAtom);

  const [activeItemId, setActiveItemId] = useState<UniqueIdentifier | null>(null);

  const { data: extractionTemplates } = useQuery(extractionTemplatesQuery(organizationId));

  const createTemplate = useCreateExtractionsListTemplate();
  const createFolder = useCreateFolder();
  const isPending = createTemplate.isPending || createFolder.isPending;

  const resetAndNavigate = (templateId: string) => {
    trackPosthogEvent('EXTRACTIONS_TEMPLATE_CREATED');
    setName('');
    setMode('createNew');
    setSelectedTemplateId(null);
    setExtractions([]);
    setSaveInFolder(false);
    setFolderName('');
    stepper.goToFirst();
    navigate({ to: '/settings/templates/$templateId', params: { templateId } });
  };

  return (
    <StepperStep
      id="add_extractions"
      onFormSubmit={() => {
        if (!extractions.length) return;
        if (saveInFolder && !folderName.trim()) return;

        createTemplate.mutate(
          { organizationId, payload: { name, templates: extractions } },
          {
            onSuccess: (result) => {
              const templateId = result?.id;
              if (!templateId) return;
              if (saveInFolder && folderName.trim()) {
                createFolder.mutate(
                  { name: folderName.trim(), templateListIds: [templateId] },
                  { onSuccess: () => resetAndNavigate(templateId) },
                );
                return;
              }
              resetAndNavigate(templateId);
            },
          },
        );
      }}
      className={'max-md:rounded-none max-md:border-transparent'}
    >
      <DndContext
        onDragStart={({ active }) => setActiveItemId(active.id)}
        onDragEnd={({ active, over }) => {
          if (!over) return;
          const activeId = String(active.id);
          const overId = String(over.id);
          const activeIndex = extractions.findIndex((item) => item.id === activeId);
          const overIndex = extractions.findIndex((item) => item.id === overId);
          if (activeIndex !== -1 && overIndex !== -1) {
            if (activeIndex !== overIndex) {
              setExtractions((prev) => arrayMove(prev, activeIndex, overIndex));
            }
            return;
          }
          const template = extractionTemplates?.find((item) => item.id === activeId);
          if (!template) return;
          const insertIndex = overIndex !== -1 ? overIndex : extractions.length;
          const newItem: DroppedItem = {
            userQuery: template.userQuery,
            customTopicTemplateId: template.id,
            customTopicTemplateOrder: insertIndex,
            id: `dropped-${template.id}`,
          };
          setExtractions((prev) => {
            const copy = [...prev];
            copy.splice(insertIndex, 0, newItem);
            return copy;
          });
        }}
      >
        <div className="p-4 flex items-center gap-2">
          <H1 className="max-md:text-base max-md:line-clamp-1">{name}</H1>
          <Menu>
            <MenuTrigger
              render={
                <Button kind="icon" variant="ghost">
                  <Icon icon={MoreHorizontalIcon} />
                </Button>
              }
            />
            <MenuPopup align="start">
              <MenuItem onClick={() => stepper.goBack()}>
                <Icon icon={PencilEdit02Icon} />
                {t({ en: 'Edit name', nb: 'Endre navn' })}
              </MenuItem>
            </MenuPopup>
          </Menu>
          <Button
            className="ml-auto"
            variant="secondary"
            kind="with-icon"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <Icon icon={PlusSignIcon} />
            {t({ en: 'New extraction', nb: 'Ny uttrekk' })}
          </Button>
        </div>
        <div className="grid @4xl:grid-cols-2 min-h-0 @4xl:divide-y-0 divide-y @4xl:divide-x grow border-t">
          <ExtractionDropzone />
          <SavedExtractionsList />
        </div>
        <StepperNavigation className="flex max-md:flex-col p-4 rounded-b-[inherit] bg-background z-50">
          <div className="flex items-center flex-wrap gap-3 w-full">
            <Field className={'w-fit'}>
              <FieldLabel className="flex items-center gap-2 mb-0 text-sm cursor-pointer">
                <Checkbox checked={saveInFolder} onCheckedChange={setSaveInFolder} />
                {t({ en: `Save "${name}" in a folder`, nb: `Lagre "${name}" i en mappe` })}
              </FieldLabel>
            </Field>
            {saveInFolder && (
              <Field className="flex-1 min-w-xs md:max-w-xs">
                <FieldControl
                  size="sm"
                  value={folderName}
                  onValueChange={setFolderName}
                  placeholder={t({ en: 'Folder name', nb: 'Mappenavn' })}
                  required
                />
              </Field>
            )}
          </div>
          <StepperBack />
          <StepperNext
            pending={isPending}
            disabled={!extractions.length || isPending || (saveInFolder && !folderName.trim())}
          >
            {t({ en: 'Create template', nb: 'Opprett uttrekksmal' })}
          </StepperNext>
        </StepperNavigation>
        {createPortal(
          <DragOverlay>
            {activeItemId ? (
              <SavedExtractionsListItem
                id="active-item"
                title={
                  (activeItemId.toString().includes('dropped')
                    ? extractionTemplates?.find((item) => `dropped-${item.id}` === activeItemId)
                        ?.userQuery
                    : extractionTemplates?.find((item) => item.id === activeItemId)?.userQuery) ??
                  ''
                }
                disabled={false}
                dragOverlay
              />
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </StepperStep>
  );
}
